import { makeVar, useReactiveVar } from "@apollo/client"
import { appStorage } from "./storage"
import { parseLocalStorageValueAndFilterOutOldIffContainsDate, updateReactiveVarAndAddToLocalStorage } from "./appVars"

/** Indicates whether the user has activated speedboost */
export const hasSpeedBoost = makeVar<boolean | undefined>(undefined)
export const useHasSpeedBoost = () => useReactiveVar(hasSpeedBoost)

/** Indicates whether a user has access to cost-transfer or not.

 If the user does not have access, relevant GUI-elements should be hidden from view.
 */
export const hasCostTransferAccess = makeVar<boolean | undefined | "loading" | Error>(undefined)
export const useHasCostTransferAccess = () => useReactiveVar(hasCostTransferAccess)

export interface OrderQueueItem {
  id: string
  skipReFetchObservableQueries?: boolean | undefined
  date?: Date
}

/**
 * used by CustomerOrderBadge to get orders that should be pulled for.
 * @param order
 */
export const pushOrderQueueItem = (order: { id: string; skipReFetchObservableQueries?: boolean | undefined }) => {
  if (!order?.id) {
    return
  }
  myUncompletedOrders.add(order)
}

/**
 * used by CustomerOrderBadge to remove orders that are completed.
 * @param order
 */
export const removeOrderQueueItem = (order: { id: string }) => {
  myUncompletedOrders.remove(order.id)
}

export const useOrderQueueItems = () => useReactiveVar(myUncompletedOrders.var)

const uncompletedOrdersVar = makeVar<Record<string, OrderQueueItem> | null>(null)
const uncompletedOrdersUpdater = updateReactiveVarAndAddToLocalStorage(uncompletedOrdersVar,  "myRegisteredOrders")

appStorage.get({ key: "myRegisteredOrders" }).then((v) => {
  if (!v.value) {
    return
  }
  const reduced = parseLocalStorageValueAndFilterOutOldIffContainsDate<OrderQueueItem>(v.value)
  uncompletedOrdersVar(reduced)
})

export const myUncompletedOrders = {
  var: uncompletedOrdersVar,
  update: uncompletedOrdersUpdater,
  remove: (orderId: string) =>
    uncompletedOrdersUpdater((state) => {
      if (!state) {
        return {}
      }
      const { [orderId]: _omit, ...rest } = state
      return rest
    }),
  clear: () => uncompletedOrdersUpdater({}),
  add: (order: OrderQueueItem) =>
    uncompletedOrdersUpdater((s) => ({
      ...s,
      // @ts-ignore: source is specified more than once
      [order.id]: { source: "localStorage", ...order },
    })),
}
